import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function focusable(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
}

/**
 * Modal plumbing for a `role="dialog"`: focus the first control on open, keep Tab
 * inside, close on Escape, and hand focus back to whatever opened it.
 */
export function useFocusTrap<T extends HTMLElement>(onClose: () => void): RefObject<T | null> {
  const ref = useRef<T>(null);
  // Held in a ref so a parent's inline arrow does not re-run the effect and steal focus back.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // The container takes focus when it is focusable, so opening the dialog does not
    // trip a control's own popup; otherwise the first control does.
    const box = ref.current;
    if (box) (box.hasAttribute("tabindex") ? box : focusable(box)[0])?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== "Tab" || !ref.current) return;
      const items = focusable(ref.current);
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const outside = !ref.current.contains(active);
      if (event.shiftKey ? active === first || outside : active === last || outside) {
        (event.shiftKey ? last : first).focus();
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);

  return ref;
}
